import { Button, DownIcon, Menu, MenuItem } from "@carrotfertility/carotene-core"
import { usePreferredLocaleContext } from "components/context/user/PreferredLocaleContext"
import React, { useEffect, useState } from "react"
import { SupportedLocale, getSupportedLocaleDisplayName, useAllLocales, isValidLocale } from "./app/IntlMessages"
import { useIntl } from "react-intl"
import { PreferredLocaleChangedDialog } from "./register/PreferredLocaleChangedDialog"
import queryString from "query-string"
import { useLocation } from "react-router"

interface UnauthPreferredLocaleProps {
  showModal?: boolean
}

function UnauthPreferredLocale({ showModal }: UnauthPreferredLocaleProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { setPreferredLocale } = usePreferredLocaleContext()
  const intl = useIntl()
  const locale: SupportedLocale = intl.locale as SupportedLocale
  const [modalOpen, setModalOpen] = React.useState(false)
  const location = useLocation()
  const langFromParams = queryString.parse(location.search).lang

  // NOTE: this is a hack that means the selector only shows up if the most recently logged-out user has a language other than english
  //  selected. This is a way to make sure it isn't visible for members since the language selection that allows non-english selection *is* behind a flag
  //  will be removed in https://carrotfertility.atlassian.net/browse/WQ-1422
  const { lastUsedLocale } = JSON.parse(localStorage.getItem("intl")) ?? {}
  const showPreferredLocaleSelect = showModal ?? lastUsedLocale !== "en-US"

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (selectedLocale?: SupportedLocale) => {
    // this gets passed a SyntheticEvent if user clicks out of dropdown, don't change locale in that case
    if (selectedLocale && typeof selectedLocale === "string") {
      setPreferredLocale(selectedLocale)
      if (showModal) {
        setModalOpen(true)
      }
    }
    setAnchorEl(null)
  }

  const locales: ReadonlyArray<SupportedLocale> = useAllLocales()

  useEffect(() => {
    if (typeof langFromParams === "string" && isValidLocale(langFromParams)) {
      setPreferredLocale(langFromParams)
    }
  }, [setPreferredLocale, langFromParams, locales])

  return (
    <>
      {showPreferredLocaleSelect && (
        <>
          <Button
            variant="outlined"
            color="secondary"
            id="show-menu"
            aria-label={intl.formatMessage({ defaultMessage: "Open menu" })}
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup={true}
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            {getSupportedLocaleDisplayName(locale)}
            <DownIcon />
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "show-menu"
            }}
          >
            {locales.map((locale) => {
              const label = getSupportedLocaleDisplayName(locale)
              return (
                <MenuItem key={locale} value={label} onClick={() => handleClose(locale)}>
                  {label}
                </MenuItem>
              )
            })}
          </Menu>
          {/* For contexts like Registration, we want to show a modal giving user more info about language selection */}
          {showModal && (
            <PreferredLocaleChangedDialog preferredLocale={locale} modalOpen={modalOpen} setModalOpen={setModalOpen} />
          )}
        </>
      )}
    </>
  )
}

export { UnauthPreferredLocale }
